"use client"

import * as React from "react"
import { ChevronDownIcon } from "lucide-react"
import { Calendar } from "@/components/ui/calendar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export function CalendarDateTime({ 
  value, 
  onChange, 
  placeholder = "Select date & time",
  className,
  disabled,
  showTimeInput = true 
}) {
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState(value)
  const [time, setTime] = React.useState(() => {
    if (value) {
      const hours = value.getHours().toString().padStart(2, '0')
      const minutes = value.getMinutes().toString().padStart(2, '0')
      const seconds = value.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }
    return "10:30:00"
  })

  React.useEffect(() => {
    setDate(value)
    if (value) {
      const hours = value.getHours().toString().padStart(2, '0')
      const minutes = value.getMinutes().toString().padStart(2, '0')
      const seconds = value.getSeconds().toString().padStart(2, '0')
      setTime(`${hours}:${minutes}:${seconds}`)
    }
  }, [value])

  const handleDateSelect = (selectedDate) => {
    if (!selectedDate) return
    
    // Combine date with current time
    const [hours, minutes, seconds] = time.split(':').map(Number)
    const newDateTime = new Date(selectedDate)
    newDateTime.setHours(hours, minutes, seconds || 0)
    
    setDate(newDateTime)
    onChange?.(newDateTime)
    if (!showTimeInput) {
      setOpen(false)
    }
  }

  const handleTimeChange = (e) => {
    const newTime = e.target.value
    setTime(newTime)
    
    if (date) {
      const [hours, minutes, seconds] = newTime.split(':').map(Number)
      const newDateTime = new Date(date)
      newDateTime.setHours(hours, minutes, seconds || 0)
      onChange?.(newDateTime)
    }
  }

  const formatDateTime = (dt) => {
    if (!dt) return placeholder
    const dateStr = dt.toLocaleDateString()
    if (showTimeInput) {
      const timeStr = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      return `${dateStr} ${timeStr}`
    }
    return dateStr
  }

  return (
    <div className="flex gap-4">
      <div className="flex flex-col flex-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={cn(
                "justify-between font-normal bg-[#111318] border-white/10 text-zinc-300 hover:bg-[#111318] hover:text-zinc-100",
                !date && "text-zinc-500",
                className
              )}
            >
              {showTimeInput ? (date ? date.toLocaleDateString() : "Select date") : formatDateTime(date)}
              <ChevronDownIcon className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto overflow-hidden p-0 bg-[#0a0a0a] border-white/10" 
            align="start"
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              onSelect={handleDateSelect}
              className="rounded-md"
            />
          </PopoverContent>
        </Popover>
      </div>
      {showTimeInput && (
        <div className="flex flex-col">
          <Input
            type="time"
            id="time-picker"
            step="1"
            value={time}
            onChange={handleTimeChange}
            disabled={disabled}
            className="bg-[#111318] border-white/10 text-zinc-300 appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
          />
        </div>
      )}
    </div>
  )
}
